import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * MY — Customer Due Diligence (CDD) chatflow — M32210.
 *
 * 문서-뷰 Preset — 3가지 CDD 문서 유형(Add CDD 로 다중 추가):
 *   1. Individual            — 개인 고객 (Full Name ✨ 로 officer/contact 자동채움)
 *   2. Legal Person          — 법인 고객 (회사명/등록번호/Business Type)
 *   3. Legal Arrangement     — 신탁 등 법적 약정
 *
 * 흐름:
 *   input-title → select-entity(선택, skip 가능) → confirm (panel 에서 CDD 입력 + Create Task)
 *   ── Person Lookup ── (Individual Full Name ✨: entity 선택 시 Officer, 미선택 시 Contact)
 *   officer-role → officer-list → officer-detail
 *   ── Address chooser ──
 *   address-list
 *
 * SKILL.md `my-preset` 의 표준 7-step + handleEvent 8 핸들러 답습.
 */
export const cddFlow: ChatFlow = {
  id: 'cdd',
  taskCodeMatch: ['M32210'],
  categoryMatch: ['customer due diligence', 'cdd'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to the Malaysia **Customer Due Diligence (CDD)** preset.\n\n` +
          `Complete the customer details on the right panel. For an **Individual** customer, click ` +
          `**Full Name ✨** to look up and auto-fill a person — from the linked entity's officers, or ` +
          `from your contacts if no entity is linked. For a **Legal Person** or **Legal Arrangement**, ` +
          `enter the company / arrangement details directly.\n\n` +
          `To prepare CDD for more than one customer or type, click **Add CDD** — each adds another ` +
          `document (Individual / Legal Person / Legal Arrangement). You can also rename each ` +
          `**Document Title**.\n\n` +
          `When all information is complete, click **Create Task** to generate the documents ` +
          `(${docCount} document(s) in this preset).\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage:
        'Search and select the **entity** to link this CDD to. ' +
        'You can skip this if the customer has no linked entity.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        allowSkip: true,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (CDD).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'choose-doc-type',
      aiMessage:
        'Now choose which **CDD document** to prepare — **Individual**, **Legal Person**, or ' +
        '**Legal Arrangement**.',
      widget: 'cdd-doc-type',
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Your CDD document is ready to fill on the right. For an **Individual**, use ' +
        '**Full Name ✨** to auto-fill a person from officers/contacts. Click **Add CDD** to ' +
        'prepare more, and **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    // ── Person Lookup steps (Director / Member / Transferor / Transferee 공통) ──
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage:
        'Person details loaded. The form has been pre-filled. You can also drag/click the fields below.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    // ── Address chooser ──
    {
      id: 'address-list',
      aiMessage: 'This person/entity has multiple addresses. Pick the one to use.',
      widget: 'address-options',
      awaitEvent: true,
    },
    // ── Address 직접 입력 (문서 주소 아이콘 📍 → 챗봇 주소 폼) ──
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    // 1) Task Title
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          { role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    // 2) Entity 선택
    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      // Change(재선택)이거나 이미 문서 유형을 고른 뒤면 문서 유형 단계 생략 → confirm
      const skipDocType = !!(context.formData.cddDocTypeDone || context.formData.entityReselect);
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || '',
          entitySkipped: false,
          entityReselect: false,
        },
        nextStepId: skipDocType ? 'confirm' : 'choose-doc-type',
      };
    }

    // 2a) Entity Skip (CDD only — linked entity 없이 CDD 진행)
    if (eventName === 'entity-skip' && context.currentStepId === 'select-entity') {
      const skipDocType = !!(context.formData.cddDocTypeDone || context.formData.entityReselect);
      return {
        messages: [
          { role: 'user', content: 'Skipped entity — continue without a linked company.', timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd: '',
          entityName: '',
          icpCd: '',
          registrationNo: '',
          tlinkData: null,
          entitySkipped: true,
          entityReselect: false,
        },
        nextStepId: skipDocType ? 'confirm' : 'choose-doc-type',
      };
    }

    // 2b) 문서 유형 선택 (챗봇 내 3택) → 패널이 해당 문서 추가(호스트에서 addDoc), 챗은 confirm 으로.
    if (eventName === 'cdd-doc-type-select' && context.currentStepId === 'choose-doc-type') {
      const key = typeof payload === 'string' ? payload : payload?.key || '';
      const label =
        key === 'legal_person' ? 'Legal Person' : key === 'legal_arrangement' ? 'Legal Arrangement' : 'Individual';
      return {
        messages: [
          { role: 'user', content: `Document type: **${label}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: { cddDocTypeDone: true },
        nextStepId: 'confirm',
      };
    }

    // 3) Panel 의 AI 아이콘(✨) 클릭 → 사람 lookup 진입
    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      // 역할(defaultRole) 미지정 → 먼저 "어떤 종류의 임원" 인지 선택하게 함.
      if (!ctx.defaultRole) {
        return {
          messages: [
            { role: 'user', content: `Look up a person for **${ctx.fieldGroup || 'this field'}**.`, timestamp: Date.now(), stepId: 1 },
          ],
          nextStepId: 'officer-role',
          nextStepWidgetData: { entityCd },
        };
      }
      // 역할 지정 시 → 곧바로 그 역할의 임원 목록.
      return {
        messages: [
          { role: 'user', content: `Looking up **${ctx.defaultRole}**...`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
      };
    }

    // 4) Officer Role 선택
    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [
          { role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    // 5) Officer 선택
    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          { role: 'user', content: `Selected person: **${officerName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'officer-detail',
      };
    }

    // 6) officer-list 뒤로가기
    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    // 6b) 주소 직접 입력 요청 (문서 주소 아이콘) → 챗봇에 주소 폼(끝에 새 메시지로 추가)
    if (eventName === 'request-address-input') {
      return {
        messages: [
          { role: 'user', content: 'Enter an address.', timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-input',
      };
    }

    // 7) Address lookup 요청
    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [
          { role: 'user', content: `Choose an address for **${subject}**.`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    // 8) address option 선택
    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${opt.label || opt.type}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
